import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function TeamManageDialog({ team, projectName, onClose }) {
  const [name, setName] = useState(team.name || "");
  const [memberIds, setMemberIds] = useState(team.member_ids || []);
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    api
      .get("/users")
      .then(({ data }) =>
        setUsers((data || []).filter((u) => u.active !== false)),
      )
      .catch(() => {});
  }, []);

  const save = async () => {
    if (!name.trim()) {
      toast.error("Takım adı boş olamaz");
      return;
    }
    if (memberIds.length === 0) {
      toast.error("Takımda en az bir üye olmalı");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/teams/${team.id}`, {
        name: name.trim(),
        member_ids: memberIds,
      });
      toast.success("Takım güncellendi");
      onClose(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Takım güncellenemedi");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/teams/${team.id}`);
      toast.success("Takım silindi");
      onClose(true);
    } catch {
      toast.error("Takım silinemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="max-w-md" data-testid="team-manage-dialog">
        <DialogHeader>
          <DialogTitle>Takım Yönetimi</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {projectName && (
            <p className="rounded-md bg-zinc-50 px-3 py-2 text-xs text-zinc-500">
              Bağlı liste: <span className="font-medium text-zinc-700"># {projectName}</span>
            </p>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Takım Adı
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
              data-testid="team-manage-name-input"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500">
              Üyeler ({memberIds.length})
            </Label>
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Kişi ara…"
              className="mt-1 h-8 text-xs"
              data-testid="team-manage-member-search"
            />
            <div
              className="mt-1 max-h-44 overflow-y-auto rounded-md border border-zinc-200 p-1"
              data-testid="team-manage-member-list"
            >
              {users
                .filter((u) =>
                  (u.name || "")
                    .toLocaleLowerCase("tr")
                    .includes(query.toLocaleLowerCase("tr")),
                )
                .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"))
                .map((u) => (
                  <label
                    key={u.id}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-zinc-700 hover:bg-zinc-50"
                    data-testid={`team-manage-member-option-${u.id}`}
                  >
                    <Checkbox
                      checked={memberIds.includes(u.id)}
                      onCheckedChange={(v) =>
                        setMemberIds((cur) =>
                          v ? [...cur, u.id] : cur.filter((x) => x !== u.id),
                        )
                      }
                    />
                    <span className="truncate">{u.name}</span>
                  </label>
                ))}
            </div>
            {memberIds.length < 2 && (
              <p className="mt-1 text-[11px] text-amber-600">
                2'den az üyeli takımlar sol menüdeki "Takımlar" bölümünde
                görünmez.
              </p>
            )}
          </div>
          <div className="flex items-center justify-between gap-2">
            {confirmDelete ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-red-600">Emin misiniz?</span>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  onClick={remove}
                  disabled={saving}
                  data-testid="team-manage-delete-confirm"
                >
                  Evet, sil
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setConfirmDelete(false)}
                >
                  Vazgeç
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setConfirmDelete(true)}
                data-testid="team-manage-delete-btn"
                className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Takımı sil
              </Button>
            )}
            <div className="flex gap-2">
              <Button type="button" variant="ghost" onClick={() => onClose(false)}>
                İptal
              </Button>
              <Button
                type="button"
                onClick={save}
                disabled={saving || !name.trim()}
                data-testid="team-manage-save-btn"
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                Kaydet
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
